// utils/excelUtils.js
import * as XLSX from 'xlsx';
import { ALL_COLUMNS } from './tableConstants';

/**
 * Fetches all records from the API with current filters
 */
export const fetchAllRecords = async (filters = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    // Build query parameters - exclude page and limit to get all records
    const params = new URLSearchParams();
    
    if (filters.searchField && filters.searchValue) {
      params.append('searchField', filters.searchField);
      params.append('searchValue', filters.searchValue);
    }
    if (filters.courseType) params.append('courseType', filters.courseType);
    if (filters.courseName) params.append('courseName', filters.courseName);
    if (filters.branch) params.append('branch', filters.branch);
    
    // Add a special parameter to indicate we want all records
    params.append('all', 'true');
    
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/students-record/download?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch all records');
    }

    const result = await response.json();
    return result.data || result.students || result; // Adjust based on your API response structure
  } catch (error) {
    console.error('Error fetching all records:', error);
    throw error;
  }
};

/**
 * Downloads Excel file with all records
 */
export const downloadExcel = async (currentData, selectedColumns, filters = {}) => {
  try {
    // Show loading indicator
    const loadingToast = document.createElement('div');
    loadingToast.textContent = 'Fetching all records for download...';
    loadingToast.style.cssText = 'position:fixed;top:20px;right:20px;background:#4CAF50;color:white;padding:15px;border-radius:5px;z-index:10000';
    document.body.appendChild(loadingToast);

    // Fetch all records with current filters
    const allData = await fetchAllRecords(filters);
    
    // Remove loading indicator
    document.body.removeChild(loadingToast);

    // Ensure data is an array before processing
    if (!Array.isArray(allData) || allData.length === 0) {
      alert('No data available to download.');
      return;
    }
    
    const visibleColumns = ALL_COLUMNS.filter(col => selectedColumns.includes(col.key));
    const headers = visibleColumns.map(col => col.label);
    
    const excelData = allData.map(item => 
      visibleColumns.map(col => {
        const value = item[col.key];
        
        if (value === null || value === undefined || value === 'null') {
          return '';
        } else {
          return value.toString();
        }
      })
    );
    
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    
    const colWidths = headers.map(header => ({
      wch: Math.max(
        header.length,
        ...excelData.map(row => row[headers.indexOf(header)]?.length || 0),
        15
      )
    }));
    ws['!cols'] = colWidths;
    
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true },
        alignment: { horizontal: "center" }
      };
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Student Records');
    
    const currentDate = new Date().toISOString().split('T')[0];
    const fileName = `Student_Records_Report_${currentDate}.xls`;
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xls', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.ms-excel' 
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Show success message
    alert(`Successfully downloaded ${allData.length} records!`);
  } catch (error) {
    console.error('Error generating Excel file:', error);
    alert('There was an error generating the Excel file. Please try again.');
  }
};

export const formatValue = (value) => {
  if (value === null || value === undefined || value === 'null' || value === '') {
    return '';
  }
  return value;
};